import { toZonedTime } from 'date-fns-tz'
import { DateTime } from 'luxon'

/**
 * Gets timezones with formatted labels for display purposes
 * @returns Array of timezone objects with value and label properties
 */
export function getTimezoneOptions(): Array<{ value: string; label: string }> {
  const timezones = getSupportedTimezones().map((timezone) => ({
    timezone,
    offset: getTimezoneOffset(new Date(), timezone),
  }))
  const sortedTimezones = timezones.sort((a, b) => a.offset - b.offset)
  return sortedTimezones.map((timezone) => {
    // Convert offset from minutes to hours and minutes
    const offsetInMinutes = timezone.offset
    const offsetHours = Math.floor(Math.abs(offsetInMinutes) / 60)
    const offsetMinutes = Math.abs(offsetInMinutes) % 60
    const offsetSign = offsetInMinutes >= 0 ? '+' : '-'
    const offsetString =
      offsetMinutes === 0
        ? `${offsetSign}${offsetHours.toString().padStart(2, '0')}:00`
        : `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`
    const label = `(GMT${offsetString}) ${timezone.timezone.replace(/_/g, ' ')}`

    return {
      value: timezone.timezone,
      label,
    }
  })
}

/**
 * Human-readable label for an IANA timezone, matching dropdown option style.
 * Uses referenceDate so the GMT offset reflects DST when relevant.
 */
export function formatTimezoneDisplayLabel(ianaTimezone: string, referenceDate: Date): string {
  if (!ianaTimezone?.trim()) {
    return ''
  }
  try {
    if (!isValidIANATimezone(ianaTimezone)) {
      return ianaTimezone.replace(/_/g, ' ')
    }
    const offsetInMinutes = getTimezoneOffset(referenceDate, ianaTimezone)
    const offsetHours = Math.floor(Math.abs(offsetInMinutes) / 60)
    const offsetMinutes = Math.abs(offsetInMinutes) % 60
    const offsetSign = offsetInMinutes >= 0 ? '+' : '-'
    const offsetString =
      offsetMinutes === 0
        ? `${offsetSign}${offsetHours.toString().padStart(2, '0')}:00`
        : `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`
    return `(GMT${offsetString}) ${ianaTimezone.replace(/_/g, ' ')}`
  } catch {
    return ianaTimezone.replace(/_/g, ' ')
  }
}

/**
 * Gets all supported timezones from the system using Intl API
 * @returns Array of IANA timezone identifiers
 */
export function getSupportedTimezones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch (error) {
    // Fallback to a minimal set if Intl.supportedValuesOf is not available
    console.warn('Intl.supportedValuesOf not available, using fallback timezones')
    return [
      'UTC',
      'America/New_York',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Paris',
      'Asia/Tokyo',
      'Australia/Sydney',
    ]
  }
}

/**
 * Validates if a string is a valid IANA timezone identifier
 * @param timezone - The timezone string to validate
 * @returns true if valid, false otherwise
 */
export function isValidIANATimezone(timezone: string): boolean {
  if (!timezone || typeof timezone !== 'string') {
    return false
  }

  // Check if it's in the supported timezones list
  const supportedTimezones = getSupportedTimezones()
  if (supportedTimezones.includes(timezone)) {
    return true
  }

  // Additional validation: check if it follows IANA format
  // IANA timezones typically follow the pattern: Continent/City or Continent/Region/City
  const ianaPattern = /^[A-Za-z_]+\/[A-Za-z_]+(\/[A-Za-z_]+)*$/
  if (!ianaPattern.test(timezone)) {
    return false
  }

  // Try creating a date in the timezone to see if it's valid
  try {
    const testDate = new Date()
    const zonedDate = toZonedTime(testDate, timezone)
    return !isNaN(zonedDate.getTime())
  } catch {
    return false
  }
}

/**
 * Gets the timezone offset as a display string (e.g. "-03:00", "+01:00")
 * @param date - The date to check
 * @param timezone - The IANA timezone identifier
 * @returns Offset string in ±HH:MM format
 */
export function getTimezoneOffsetString(date: Date, timezone: string): string {
  const offsetInMinutes = getTimezoneOffset(date, timezone)
  const offsetHours = Math.floor(Math.abs(offsetInMinutes) / 60)
  const offsetMinutes = Math.abs(offsetInMinutes) % 60
  const offsetSign = offsetInMinutes >= 0 ? '+' : '-'
  return offsetMinutes === 0
    ? `${offsetSign}${offsetHours.toString().padStart(2, '0')}:00`
    : `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`
}

/**
 * Gets the timezone offset in minutes for a specific date and timezone
 * @param date - The date to check
 * @param timezone - The IANA timezone identifier
 * @returns Offset in minutes (positive for UTC+, negative for UTC-)
 */
export function getTimezoneOffset(date: Date, timezone: string): number {
  if (!isValidIANATimezone(timezone)) {
    throw new Error(`Invalid timezone: ${timezone}`)
  }

  try {
    const luxonDate = DateTime.fromJSDate(date).setZone(timezone)
    if (!luxonDate.isValid) {
      throw new Error(`Invalid date or timezone: ${date}, ${timezone}`)
    }

    const offsetInMinutes = luxonDate.offset
    return offsetInMinutes
  } catch (error) {
    console.error(`Error getting timezone offset for ${date} in ${timezone}:`, error)
    throw error
  }
}
